// src/utils/redactTokens.js
// Remove credentials from what a problem report stores (Roadmap #90; Invitations Build 2 addendum 3
// item 2). An invitation or join link, a sign-in callback or a verification link is a credential:
// whoever holds it can use it. A problem report is read by admins, pushed to Deck and emailed in a
// digest, so it must never carry one.
//
// Two levels:
//   redactText(text)     — free text (the reporter's own words, error messages): token SHAPES only —
//                          invitation routes, verification/deletion path segments, signed tokens,
//                          JWT-like strings. Ordinary words such as "error code=500" are left alone.
//   redactAddress(text)  — an address (page URL, hash, query): the shapes, plus the VALUES of the
//                          parameters that carry credentials (after ?, & or #, since Supabase's
//                          implicit-flow callback puts them in the fragment).
//   redactDebugContext(obj) — the debug object, field by field, before it is stringified: address
//                          fields get redactAddress, everything else redactText.
// Every writer of error_reports must pass its address, debug context and feedback through here
// (today there is one: src/components/core/ErrorReportModal.jsx).

import { TOKEN_SHAPE_SOURCE } from './authFlow';

export const REDACTED = '[redacted]';

// The invitation pages' route segment: #/invite/<token>, #/join/<token>, and the e-mail action pages.
const INVITATION_ROUTE = /(#\/(?:invite|join|invitation-optout|invitation-report)\/)[^?&#\s"'\\]+/g;
// Path segments that carry a token: /verify-authority/<token>, and the retired /demo/<slug>/delete/<token>.
const TOKEN_PATH = /(\/(?:verify-authority|delete)\/)[^/?&#\s"'\\]+/g;
// A signed invitation or join-link token anywhere, by the routes' own shape (authFlow.js).
const SIGNED_TOKEN = new RegExp(`(?<![0-9a-fA-F])${TOKEN_SHAPE_SOURCE}(?![0-9a-fA-F])`, 'g');
// A JWT (Supabase access tokens and the like).
const JWT = /eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
// Parameters whose value is a credential (addresses only): Supabase auth callbacks (implicit and
// PKCE flows), and the sign-up carrier's invitation and join-link tokens (authFlow.js).
const CREDENTIAL_PARAM = /([?&#](?:access_token|refresh_token|provider_token|provider_refresh_token|token_hash|token|code|invite|join)=)[^&#\s"'\\]*/g;

export function redactText(text) {
  if (typeof text !== 'string' || text === '') return text;
  return text
    .replace(INVITATION_ROUTE, `$1${REDACTED}`)
    .replace(TOKEN_PATH, `$1${REDACTED}`)
    .replace(SIGNED_TOKEN, REDACTED)
    .replace(JWT, REDACTED);
}

export function redactAddress(text) {
  if (typeof text !== 'string' || text === '') return text;
  return redactText(text.replace(CREDENTIAL_PARAM, `$1${REDACTED}`));
}

// Fields of the debug context that hold (parts of) the page address.
const ADDRESS_FIELDS = new Set(['pathname', 'hash', 'search', 'urlSegments', 'href', 'url', 'page_url']);

function redactValue(value, asAddress) {
  if (typeof value === 'string') return asAddress ? redactAddress(value) : redactText(value);
  if (Array.isArray(value)) return value.map(v => redactValue(v, asAddress));
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, redactValue(v, asAddress || ADDRESS_FIELDS.has(k))])
    );
  }
  return value;
}

export function redactDebugContext(context) {
  return redactValue(context, false);
}
